import logger from '../../lib/helpers/logger';
import { formatCurrencyDisplay } from '../../lib/helpers/formatCurrencyDisplay';

interface CurrencyValueProps extends React.HTMLAttributes<HTMLDivElement> {
	useCurrencySymbol?: boolean;
	currencySymbol?: string;
	useAccountingFormat?: boolean;
	value: number | undefined;
}

/**
 * Renders a numeric amount for on-screen display.
 *
 * Formats with en-US grouping and a USD symbol by default (see {@link formatCurrencyDisplay}).
 * Wraps the result in `<bdi dir="ltr">` so negative signs and digits stay in reading order
 * when the UI is RTL (e.g. Arabic).
 */
export default function CurrencyValue({
	currencySymbol: denominator = '$',
	useCurrencySymbol = true,
	useAccountingFormat = false,
	value,
	className,
	...props
}: CurrencyValueProps) {
	if (typeof value !== 'number' || isNaN(value)) {
		logger.logGeneral.warn({}, 'CurrencyValue received a non-numeric value:', value);
		return value;
	}

	let display: string;
	if (useAccountingFormat && value < 0) {
		const absFormatted = Math.abs(value).toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
		display = `(${useCurrencySymbol ? denominator : ''}${absFormatted})`;
	} else {
		display = useCurrencySymbol
			? formatCurrencyDisplay(value, denominator)
			: value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	return (
		<div {...props} className={className}>
			<bdi dir='ltr'>
				{display}
			</bdi>
		</div>
	);
}
